// Pure page-window math for the drill-down's client-side pagination. Extracted
// from `usePagedSlice` (bundle-type-drill-down.tsx) so the index arithmetic —
// page clamp, 1-based from/to, hasPrev/hasNext — is unit-testable without
// rendering a hook.

export interface PageWindow {
  /** Page index clamped into `[0, pageCount - 1]`. */
  safePage: number
  /** Zero-based offset of the first row on the page (use with `Array.slice`). */
  start: number
  /** 1-based index of the first visible row (0 when the list is empty). */
  from: number
  /** 1-based index of the last visible row (0 when the list is empty). */
  to: number
  hasPrev: boolean
  hasNext: boolean
}

/**
 * Resolve the visible window for `page` over `total` rows at `pageSize`.
 * `page` is clamped so an out-of-range value (e.g. the list shrank under a
 * held page index) snaps to the last valid page rather than rendering blank.
 */
export function computePageWindow(total: number, page: number, pageSize: number): PageWindow {
  const size = Math.max(1, pageSize)
  const pageCount = Math.max(1, Math.ceil(total / size))
  const safePage = Math.min(Math.max(0, page), pageCount - 1)
  const start = safePage * size
  return {
    safePage,
    start,
    from: total === 0 ? 0 : start + 1,
    to: Math.min(start + size, total),
    hasPrev: safePage > 0,
    hasNext: safePage < pageCount - 1,
  }
}
